import {
  Component,
  inject,
  QueryList,
  signal,
  ViewChildren,
  ChangeDetectionStrategy,
  computed,
  input,
  model,
  afterRenderEffect,
  DestroyRef,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { CaseSpinnerComponent } from '../case-spinner/case-spinner.component';
import { ICase, IWinSkin, SpinnerAmount } from '../interfaces/cases.interface';
import { DecimalPipe } from '@angular/common';
import { SkinsListComponent } from '../../../core/components/skins-list/skins-list.component';
import { FairnessButtonComponent } from '../../../core/components/fairness-button/fairness-button.component';
import { SvgIconComponent } from 'angular-svg-icon';
import { BalanceIconComponent } from '../../../core/components/balance-icon/balance-icon.component';
import { CheckboxComponent } from '../../../core/components/checkbox/checkbox.component';
import {
  animate,
  query,
  stagger,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { BalanceType } from '@core/types/balance.type';
import { ToastrService } from 'ngx-toastr';
import { sleep } from '@core/utils/sleep.util';
import { SessionService } from '@core/services/session.service';

export interface CaseUnboxingSettings {
  spinner: {
    amount: boolean;
  };
  caseItem: {
    detailsType: 'price' | 'category';
    locked: boolean;
  };
  openButton?: {
    text: string;
    disabled: boolean;
  };
  balanceType: BalanceType;
  navigation?: {
    backText: string;
    backLink: RouterLink['routerLink'];
  };
  openSettings?: {
    skipFirstBalanceUpdate?: boolean;
  };
  openFn: (params: { amount?: number }) => Promise<IWinSkin[]>;
  afterOpenFn?: () => void;
}

@Component({
  selector: 'app-cases-unboxing',
  templateUrl: './cases-unboxing.component.html',
  styleUrls: ['./cases-unboxing.component.scss'],
  standalone: true,
  imports: [
    CaseSpinnerComponent,
    RouterLink,
    SkinsListComponent,
    FairnessButtonComponent,
    SvgIconComponent,
    BalanceIconComponent,
    DecimalPipe,
    CheckboxComponent,
  ],
  animations: [
    trigger('staggerList', [
      transition('* => *', [
        query(
          ':enter',
          [
            style({
              opacity: 0,
              transform: 'scale(0.8)',
              height: '0',
              margin: '0',
            }),
            stagger('100ms', [
              animate(
                '300ms ease',
                style({
                  opacity: 1,
                  transform: 'scale(1)',
                  height: '*',
                  margin: '*',
                })
              ),
            ]),
          ],
          { optional: true }
        ),
        query(
          ':leave',
          [
            stagger('100ms', [
              animate(
                '300ms ease',
                style({
                  opacity: 0,
                  transform: 'scale(0.8)',
                  height: '0',
                  margin: '0',
                })
              ),
            ]),
          ],
          { optional: true }
        ),
      ]),
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('300ms ease', style({ opacity: 0 })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CasesUnboxingComponent {
  private toastrService = inject(ToastrService);
  private sessionService = inject(SessionService);
  private destroyRef = inject(DestroyRef);
  caseItem = input.required<ICase>();
  config = input.required<CaseUnboxingSettings>();

  initialOpenCase = model(false);

  winItems = signal<IWinSkin[]>([]);

  spinners = computed(() =>
    Array.from({ length: this.spinnersAmount() }, (_, index) => index + 1)
  );

  @ViewChildren(CaseSpinnerComponent)
  spinnerComponents!: QueryList<CaseSpinnerComponent>;

  spinnersAmount = signal<SpinnerAmount>(1);
  fastOpen = signal(false);

  isDemo = signal(false);
  isSpinning = signal(false);

  openCaseEffect = afterRenderEffect(async () => {
    if (this.initialOpenCase()) {
      await sleep(1000);
      await this.handleOpenCase();
    }
  });

  constructor() {
    this.destroyRef.onDestroy(async () => {
      if (this.isSpinning()) {
        const { balance: bal, type } =
          await this.sessionService.getUpdatedBalance(
            this.config().balanceType
          );

        this.config().afterOpenFn?.();
        this.sessionService.setBalance(type, bal);
      }
    });
  }

  private getRandomSkinByChance(caseItem: ICase) {
    if (!caseItem.skins || caseItem.skins.length === 0) {
      throw new Error('No skins available in case');
    }

    const totalChance = caseItem.skins.reduce(
      (sum, skin) => sum + (skin.chance ?? 0),
      0
    );

    if (totalChance === 0) {
      const randomIndex = Math.floor(Math.random() * caseItem.skins.length);
      return caseItem.skins[randomIndex];
    }

    const random = Math.random() * totalChance;
    let cumulativeChance = 0;

    for (const skin of caseItem.skins) {
      cumulativeChance += skin.chance ?? 0;
      if (random <= cumulativeChance) {
        return skin;
      }
    }

    return caseItem.skins[caseItem.skins.length - 1];
  }

  private async rollSpinners() {
    this.isSpinning.set(true);
    try {
      const spinnersComponents = this.spinnerComponents.toArray();

      const rollPromises = [];
      for (let i = 0; i < spinnersComponents.length; i++) {
        rollPromises.push(
          spinnersComponents[i].roll(this.winItems()[i], this.winItems())
        );
      }

      await Promise.all(rollPromises);
    } catch (error) {
      console.log('🚀 ~ CasesUnboxingComponent ~ rollupinners ~ error:', error);
    } finally {
      this.isSpinning.set(false);
    }
  }

  public async handleStartDemoSpin() {
    this.winItems.set(
      Array.from({ length: this.spinnersAmount() }, () => {
        const randomSkin = this.getRandomSkinByChance(this.caseItem());

        return {
          skin: randomSkin?.name,
          price: randomSkin?.price ?? 0,
          casename: this.caseItem().name,
          case: this.caseItem().name,
        };
      })
    );

    this.isDemo.set(true);
    this.isSpinning.set(true);

    await this.rollSpinners();
  }

  public async handleOpenCase() {
    const response = await this.config().openFn({
      amount: this.spinnersAmount(),
    });

    if (response?.length === 0) {
      this.toastrService.error('No items received');
      throw new Error('No items received');
    }

    const { balance: bal, type } = await this.sessionService.getUpdatedBalance(
      this.config().balanceType
    );

    if (!this.config().openSettings?.skipFirstBalanceUpdate) {
      const sumOfTheDrops = response.reduce(
        (sum, item) => sum + (item.price ?? 0),
        0
      );
      this.sessionService.setBalance(type, bal - sumOfTheDrops);
    }
    this.winItems.set(response);
    await this.rollSpinners();
    this.config().afterOpenFn?.();
    this.sessionService.setBalance(type, bal);
  }

  public handleChangeSpinnersAmount(amount: SpinnerAmount) {
    this.spinnersAmount.set(amount);
  }
}
